import { useAppKit, useAppKitAccount } from '@reown/appkit/react'
import { useCallback, useState } from 'react'
import { getAddress } from 'viem'
import { useSignMessage } from 'wagmi'
import { getChainId } from 'wagmi/actions'
import { apiClient } from '@/lib/generated/api-client'
import {
	authenticateWithSiwe,
	createSiweMessage,
	detectSignatureType,
	getSiweNonce,
	getSiweUserData,
	getSiweVerify,
	refreshSiweSession,
	type SiweAuthConfig,
	type SiweAuthResult,
	verifySiweSession,
} from '@/lib/siwe-auth'
import { wagmiAdapter } from '@/provider'
import { useAuthStore } from '@/stores/authStore'

export function useSiweAuth() {
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [isAuthenticating, setIsAuthenticating] = useState(false)

	const { open } = useAppKit()
	const { address, status, embeddedWalletInfo, allAccounts } = useAppKitAccount()
	const { signMessageAsync } = useSignMessage()
	const updateUser = useAuthStore((state) => state.auth.updateUser)
	const setStatus = useAuthStore((state) => state.auth.setStatus)

	/**
	 * Get smart account address from allAccounts and embeddedWalletInfo
	 * Takes user.address from embeddedWalletInfo and finds the first different address in allAccounts
	 */
	const getSmartAccountAddress = useCallback((): string | null => {
		if (!embeddedWalletInfo?.user?.address || !allAccounts) {
			return null
		}

		const userAddress = embeddedWalletInfo.user.address

		// Find the first account in allAccounts that has a different address
		const differentAccount = allAccounts.find(
			(account) => account.address && account.address.toLowerCase() !== userAddress.toLowerCase(),
		)

		return differentAccount?.address || null
	}, [embeddedWalletInfo, allAccounts])

	/**
	 * Associate smart account with the authenticated user
	 */
	const associateSmartAccount = useCallback(
		async (smartAccountAddress: string): Promise<boolean> => {
			try {
				const response = await apiClient.POST('/api/v1/siwe/wallet/associate-smart-account/', {
					body: {
						smart_account_address: smartAccountAddress,
					},
				})

				if (response.data) {
					return true
				} else {
					console.error('Failed to associate smart account:', response.error)
					return false
				}
			} catch (error) {
				console.error('Error associating smart account:', error)
				return false
			}
		},
		[],
	)

	/**
	 * Detect if wallet is smart contract (Account Abstraction) or EOA
	 */
	const getWalletInfo = useCallback(() => {
		const isReown = typeof window !== 'undefined' && window.ethereum?.isReown
		const isMobile =
			typeof window !== 'undefined' ? /Mobi|Android/i.test(window.navigator.userAgent) : false

		// Reown web = smart contract wallet, Reown mobile = EOA
		const isSmartContractWallet = isReown && !isMobile

		return {
			isSmartContractWallet,
		}
	}, [])

	/**
	 * Create SIWE message string for signing while keeping original message as object
	 * This follows the standard SIWE format for signing
	 */
	const createSiweMessageString = useCallback((message: any): string => {
		return `${message.domain} wants you to sign in with your Ethereum account:
${message.address}

${message.statement}

URI: ${message.uri}
Version: ${message.version}
Chain ID: ${message.chain_id}
Nonce: ${message.nonce}
Issued At: ${message.issued_at}`
	}, [])

	/**
	 * Sign SIWE message - works for both EOA and smart contract wallets
	 * Message stays as object, signature is string
	 */
	const signSiweMessage = useCallback(
		async (message: any): Promise<string> => {
			try {
				// Create SIWE message string for signing
				const siweMessageString = createSiweMessageString(message)

				// Sign the message (wagmi handles both EOA and smart contract wallets)
				// This should work for both social login and EOA wallets
				const signature = await signMessageAsync({
					message: siweMessageString,
				})

				return signature
			} catch (error) {
				console.error('Error signing SIWE message:', error)
				throw error
			}
		},
		[signMessageAsync, createSiweMessageString],
	)

	// Default SIWE configuration
	const defaultConfig: SiweAuthConfig = {
		domain: typeof window !== 'undefined' ? window.location.host : 'localhost:3000',
		uri: typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000',
		statement: 'Sign in with Ethereum to continue to GaiaDEX',
		version: '1',
		chain_id: getChainId(wagmiAdapter.wagmiConfig),
	}

	/**
	 * Determine if user has wallet connected (Case A) or social login (Case B)
	 */
	const getAuthMethod = useCallback(() => {
		const isWalletConnected = status === 'connected' && address
		const hasSocialLogin = embeddedWalletInfo?.user?.email || embeddedWalletInfo?.user?.username

		// Check if we have social login info (even after disconnect)
		if (hasSocialLogin) {
			return 'social' // Case B - SIWX (prioritize social login)
		} else if (isWalletConnected) {
			return 'wallet' // Case A - SIWE
		}

		return null
	}, [status, address, embeddedWalletInfo])

	/**
	 * Authenticate with SIWE (wallet-based)
	 */
	const authenticateWithWallet = useCallback(
		async (config: SiweAuthConfig = defaultConfig): Promise<SiweAuthResult> => {
			if (!address) {
				return {
					success: false,
					error: 'No wallet connected',
				}
			}

			// Prevent multiple simultaneous authentication attempts
			if (isAuthenticating || isLoading) {
				return {
					success: false,
					error: 'Authentication already in progress',
				}
			}

			try {
				setIsLoading(true)
				setIsAuthenticating(true)
				setError(null)

				// Get verify from server (optional - continue even if it fails)
				try {
					await getSiweVerify()
				} catch (error) {
					console.warn('Verify failed, continuing with authentication:', error)
				}

				// Get nonce from server
				const nonce = await getSiweNonce()

				// Create SIWE message with checksummed address
				const checksummedAddress = getAddress(address)
				const message = createSiweMessage(
					config,
					checksummedAddress,
					nonce,
					new Date().toISOString(),
				)

				// Sign the SIWE message object (message stays as object, signature is string)
				const signature = await signSiweMessage(message)

				// Authenticate with server
				const result = await authenticateWithSiwe(message, signature)

				if (result.success && result.user) {
					// Update user state
					updateUser({
						walletAddress: checksummedAddress,
						roles: result.user.roles, // Backward compatibility
						groups: result.user.groups, // New groups structure
						siweAuthenticated: true,
					})
					setStatus('authenticated')

					// Try to associate smart account if available
					const smartAccountAddress = getSmartAccountAddress()

					if (smartAccountAddress) {
						try {
							await associateSmartAccount(smartAccountAddress)
						} catch (error) {
							console.warn('Failed to associate smart account:', error)
							// Don't fail the authentication if smart account association fails
						}
					}
				}

				return result
			} catch (error) {
				console.error('Wallet authentication error:', error)
				const errorMessage = error instanceof Error ? error.message : 'Authentication failed'
				setError(errorMessage)
				return {
					success: false,
					error: errorMessage,
				}
			} finally {
				setIsLoading(false)
				setIsAuthenticating(false)
			}
		},
		[address, signSiweMessage, updateUser, setStatus, getSmartAccountAddress],
	)

	/**
	 * Authenticate with SIWX (social login-based)
	 */
	const authenticateWithSocial = useCallback(
		async (config: SiweAuthConfig = defaultConfig): Promise<SiweAuthResult> => {
			if (!embeddedWalletInfo?.user || !address) {
				return {
					success: false,
					error: 'No social login user or wallet address found',
				}
			}

			// Prevent multiple simultaneous authentication attempts
			if (isAuthenticating || isLoading) {
				return {
					success: false,
					error: 'Authentication already in progress',
				}
			}

			try {
				setIsLoading(true)
				setIsAuthenticating(true)
				setError(null)

				// Get verify from server (optional - continue even if it fails)
				try {
					await getSiweVerify()
				} catch (error) {
					console.warn('Verify failed, continuing with authentication:', error)
				}

				// Get nonce from server
				const nonce = await getSiweNonce()

				// Use the wallet address (checksummed) for social login, not email/username
				const checksummedAddress = getAddress(address)
				const message = createSiweMessage(
					config,
					checksummedAddress,
					nonce,
					new Date().toISOString(),
				)

				// Sign the SIWE message object (message stays as object, signature is string)
				const signature = await signSiweMessage(message)

				// Authenticate with server
				const result = await authenticateWithSiwe(message, signature)

				if (result.success && result.user) {
					// Update user state
					updateUser({
						walletAddress: checksummedAddress,
						email: embeddedWalletInfo.user.email,
						username: embeddedWalletInfo.user.username,
						roles: result.user.roles, // Backward compatibility
						groups: result.user.groups, // New groups structure
						siweAuthenticated: true,
					})
					setStatus('authenticated')

					// Try to associate smart account if available
					const smartAccountAddress = getSmartAccountAddress()

					if (smartAccountAddress) {
						try {
							await associateSmartAccount(smartAccountAddress)
						} catch (error) {
							console.warn('Failed to associate smart account:', error)
							// Don't fail the authentication if smart account association fails
						}
					}
				}

				return result
			} catch (error) {
				console.error('Social authentication error:', error)
				const errorMessage = error instanceof Error ? error.message : 'Authentication failed'
				setError(errorMessage)
				return {
					success: false,
					error: errorMessage,
				}
			} finally {
				setIsLoading(false)
				setIsAuthenticating(false)
			}
		},
		[embeddedWalletInfo, address, signSiweMessage, updateUser, setStatus, getSmartAccountAddress],
	)

	/**
	 * Main authentication method that automatically chooses between wallet and social
	 */
	const authenticate = useCallback(
		async (config: SiweAuthConfig = defaultConfig): Promise<SiweAuthResult> => {
			// Prevent multiple simultaneous authentication attempts
			if (isAuthenticating || isLoading) {
				return {
					success: false,
					error: 'Authentication already in progress',
				}
			}

			const authMethod = getAuthMethod()

			if (authMethod === 'wallet') {
				return authenticateWithWallet(config)
			} else if (authMethod === 'social') {
				return authenticateWithSocial(config)
			} else {
				return {
					success: false,
					error: 'No authentication method available',
				}
			}
		},
		[isAuthenticating, isLoading, getAuthMethod, authenticateWithWallet, authenticateWithSocial],
	)

	/**
	 * Refresh the authentication session
	 */
	const refresh = useCallback(async (): Promise<boolean> => {
		try {
			setIsLoading(true)
			const success = await refreshSiweSession()

			if (success) {
				// Get updated user data
				const userData = await getSiweUserData()
				if (userData) {
					updateUser({
						roles: userData.roles,
						email: userData.email,
						username: userData.username,
					})
				}
			}

			return success
		} catch (error) {
			console.error('Error refreshing session:', error)
			return false
		} finally {
			setIsLoading(false)
		}
	}, [updateUser])

	/**
	 * Verify the current session
	 */
	const verify = useCallback(async (): Promise<boolean> => {
		try {
			return await verifySiweSession()
		} catch (error) {
			console.error('Error verifying session:', error)
			return false
		}
	}, [])

	/**
	 * Get current user data
	 */
	const getUserData = useCallback(async () => {
		try {
			return await getSiweUserData()
		} catch (error) {
			console.error('Error getting user data:', error)
			return null
		}
	}, [])

	return {
		isLoading,
		error,
		authenticate,
		authenticateWithWallet,
		authenticateWithSocial,
		refresh,
		verify,
		getUserData,
		getAuthMethod,
		getWalletInfo,
		detectSignatureType,
		open,
	}
}
